import { useState } from "react";
import { Auth } from "aws-amplify";
import {
  Alert, Button, Card, Col, Form, Row
} from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ariaInvalidCheck, normaliseInteger } from "@whitespace/whitespace-utils";
import { ErrorMessages, Page, Spinner } from "@whitespace/whitespace-ui";

const ChangeAuthenticator = () => {
  const [totpConfigCompleted, setTotpConfigCompleted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [totpError, setTotpError] = useState("");
  const [totpUrl, setTotpUrl] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const schema = yup.object().shape({
    confirmationCode: yup.string().trim()
      .required("The Generated Pass Code is required")
      .min(6, "Generated Pass Code must be six or more characters")

  });

  const {
    formState: { errors }, register, handleSubmit, getValues, setValue
  } = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: "onSubmit",
    reValidateMode: "onBlur"
  });

  const setupTOTP = async () => {
    setTotpError("");
    setIsSending(true);
    Auth.currentAuthenticatedUser().then(async user => {
      await Auth.currentSession();
      Auth.setupTOTP(user).then(code => {
        setTotpCode(code);
        setTotpUrl(`otpauth://totp/AWSCognito:${user.username}?secret=${code}&issuer=${user.signInUserSession.idToken.payload.iss}`);
        setIsSending(false);
      }).catch(reason => {
        setTotpError(reason.toString());
        setIsSending(false);
      });
    }).catch(reason => {
      setTotpError(reason.toString());
      setIsSending(false);
    });
  };

  const confirmCode = () => {
    setTotpError("");
    setIsSending(true);
    const confirmedCode = getValues("confirmationCode");
    Auth.currentAuthenticatedUser().then(user => {
      Auth.verifyTotpToken(user, confirmedCode).then(() => {
        Auth.setPreferredMFA(user, "TOTP");
        setTotpConfigCompleted(true);
        setIsSending(false);
      }).catch(e => {
        setTotpError(e.toString());
        setIsSending(false);
      });
    });
  };

  return (
    <Page>
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Self Management - Authenticator Configuration
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end" />
          </Row>
        </Card.Header>
        <Card.Body className="">
          <Row>
            <Col xs={6}>
              {!totpConfigCompleted && <p>To change the authenticator application you are currently using, please start by selecting the button below.</p>}
              {totpError && (
                <Alert variant="danger" className="margin-medium" aria-atomic="true">
                  {totpError}
                </Alert>
              ) }
              {totpConfigCompleted && (
                <Alert variant="info" className="margin-medium" aria-atomic="true">
                  Your TOTP authenticator has been successfully configured.
                </Alert>
              ) }

              {!totpConfigCompleted && (

                <Button
                  onClick={() => setupTOTP()}
                  disabled={isSending}
                >
                  Set up Authenticator App
                </Button>
              )}

              {!totpConfigCompleted && totpUrl && (
                <>
                  <div style={{ alignItems: "center", width: "100%" }} className="mt-2">
                    <p>
                      Scan the QR code with your authenticator app.  Alternatively, you can manually enter the secret key into your authenticator app.
                    </p>

                    <QRCodeSVG value={totpUrl} />
                    <p className="mt-2">
                      New secret key is
                      {" "}
                      <span className="fw-bold">{totpCode}</span>
                    </p>

                  </div>
                  <div>
                    <p>Once the authenticator app has been configured, enter the pass code generated below:</p>
                    <Form autoComplete="off" onSubmit={handleSubmit(confirmCode)}>
                      <Form.Group controlId="name">
                        <Form.Label className="horiz-form-label">Generated Pass Code:</Form.Label>
                        <Form.Control
                          {...register("confirmationCode")}
                          type="text"
                          role="textbox"
                          aria-required="true"
                          aria-invalid={ariaInvalidCheck("confirmationCode", errors, null)}
                          maxLength={6}
                          className="w-auto"
                          onChange={(e:any) => { e.target.value = normaliseInteger(e.target.value); setValue("confirmationCode", e.target.value); }}
                        />
                        <ErrorMessages fieldName="confirmationCode" errors={errors} validationFailures={null} />
                      </Form.Group>
                      <Button
                        type="submit"
                        disabled={isSending}
                        className="mt-2"
                      >
                        Verify
                      </Button>
                    </Form>
                  </div>

                </>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Page>
  );
};
export default ChangeAuthenticator;
